import { HStack, Stack, Toast, Toaster } from "@chakra-ui/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { lazy, Suspense, useEffect } from "react"
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation
} from "react-router-dom"
import "./App.css"
import AppLayout from "./components/layouts/AppLayout"
import AuthLayout from "./components/layouts/AuthLayout"
import WebsiteLayout from "./components/layouts/WebsiteLayout"
import ProtectedRoute from "./components/ProtectedRoute"
import DevTools from "./components/ReactQueryDevtools"
import { AuthProvider } from "./contexts/AuthContext"
import { useUserDetails } from "./hooks/useAuthQueries"
import About from "./pages/About"
import AddProperty from "./pages/AddProperty"
import ForcePasswordChange from "./pages/auth/ForcePasswordChange"
import Login from "./pages/auth/Login"
import MagicLinkVerify from "./pages/auth/MagicLinkVerify"
import ResetPassword from "./pages/auth/ResetPassword"
import ResetPasswordConfirm from "./pages/auth/ResetPasswordConfirm"
import Signup from "./pages/auth/Signup"
import BatchImportProgress from "./pages/BatchImportProgress"
import BulkImport from "./pages/BulkImport"
import Contact from "./pages/Contact"
import CreatePortfolio from "./pages/CreatePortfolio"
import Dashboard from "./pages/Dashboard"
import EnergyUtilities from "./pages/EnergyUtilities"
import Explore from "./pages/Explore"
import HomeDetail from "./pages/HomeDetail"
import InstitutionalInvestors from "./pages/InstitutionalInvestors"
import Investors from "./pages/Investors"
import LandingPage from "./pages/LandingPage"
import MyCriteria from "./pages/MyCriteria"
import MyHomes from "./pages/MyHomes"
import Notifications from "./pages/Notifications"
import Opportunities from "./pages/Opportunities"
import Pipeline from "./pages/Pipeline"
import Portfolio from "./pages/Portfolio"
import Settings from "./pages/Settings"
import SocialLandlords from "./pages/SocialLandlords"
import Team from "./pages/Team"
import Welcome from "./pages/Welcome"
import PilotDashboard from "./pilot/pages/PilotDashboard"
import PilotPage from "./pilot/pages/PilotPage"
import PilotLayout from "./pilot/PilotLayout"
import { toaster } from "./toaster"

// Digital twin pages carry three.js, so they load only when visited.
const NewTwin = lazy(() => import("./pages/twin/NewTwin"))
const TwinPage = lazy(() => import("./pages/twin/TwinPage"))

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

const PortfolioRoute: React.FC = () => {
  const { data: userDetails } = useUserDetails()
  const isPropertyInvestment =
    userDetails?.data?.organisation?.organisationType === "property_investment"
  return isPropertyInvestment ? <Portfolio /> : <MyHomes />
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  }
})

const Root: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <Routes>
            {/* Auth Routes */}
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/beta-signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/reset-password/confirm/:uid/:token"
                element={<ResetPasswordConfirm />}
              />
              <Route
                path="/reset-password/confirm"
                element={<ResetPasswordConfirm />}
              />
              <Route path="/magic-link/:token" element={<MagicLinkVerify />} />
              <Route
                path="/force-password-change"
                element={
                  <ProtectedRoute>
                    <ForcePasswordChange />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Pilot / Embedded Widget Routes (no auth, no main nav) */}
            <Route path="/pilot/:council" element={<PilotLayout />}>
              <Route index element={<PilotPage />} />
              <Route path="dashboard" element={<PilotDashboard />} />
            </Route>

            {/* Website Routes */}
            <Route element={<WebsiteLayout />}>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<About />} />
              <Route path="/for-investors" element={<Investors />} />
              <Route
                path="/institutional-investors"
                element={<InstitutionalInvestors />}
              />
              <Route path="/social-landlords" element={<SocialLandlords />} />
              <Route path="/energy-companies" element={<EnergyUtilities />} />
              <Route path="/team" element={<Team />} />
              <Route path="/contact" element={<Contact />} />
            </Route>

            {/* Protected App Routes */}
            <Route element={<AppLayout />}>
              <Route
                path="/welcome"
                element={
                  <ProtectedRoute>
                    <Welcome />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/criteria"
                element={
                  <ProtectedRoute>
                    <MyCriteria />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/portfolio"
                element={
                  <ProtectedRoute>
                    <PortfolioRoute />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/my-homes"
                element={
                  <ProtectedRoute>
                    <MyHomes />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/opportunities"
                element={
                  <ProtectedRoute>
                    <Opportunities />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pipeline"
                element={
                  <ProtectedRoute>
                    <Pipeline />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/explore"
                element={
                  <ProtectedRoute>
                    <Explore />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Notifications />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/home/:homeId"
                element={
                  <ProtectedRoute>
                    <HomeDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/add-property"
                element={
                  <ProtectedRoute>
                    <AddProperty />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-portfolio"
                element={
                  <ProtectedRoute>
                    <CreatePortfolio />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-import"
                element={
                  <ProtectedRoute>
                    <BulkImport />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/batch-import/:batchImportId"
                element={
                  <ProtectedRoute>
                    <BatchImportProgress />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/twin/new"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={null}>
                      <NewTwin />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/twin/:jobId"
                element={
                  <ProtectedRoute>
                    <Suspense fallback={null}>
                      <TwinPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
            </Route>
          </Routes>
        </Router>
        <Toaster toaster={toaster}>
          {(toast) => (
            <Toast.Root
              type={toast.type}
              width="sm"
              bg={toast.type === "success" ? "#33534B" : undefined}
            >
              <Stack gap={1} flex={1}>
                {toast.title && (
                  <Toast.Title>{toast.title as string}</Toast.Title>
                )}
                {toast.description && (
                  <Toast.Description>
                    {toast.description as string}
                  </Toast.Description>
                )}
              </Stack>
              <HStack>
                <Toast.CloseTrigger />
              </HStack>
            </Toast.Root>
          )}
        </Toaster>
        <DevTools />
      </AuthProvider>
    </QueryClientProvider>
  )
}

export default Root
